import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlass, CaretDown, X } from '@phosphor-icons/react';

export default function SearchableSelect({ options, value, onChange, placeholder, testId, required }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Hidden input for form validation */}
      {required && <input tabIndex={-1} value={value || ''} onChange={() => {}} required
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} />}

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left bg-[#fafaf9] border border-stone-200 px-3 py-2 text-sm text-stone-900 transition-all outline-none hover:border-stone-300 focus:border-[#002FA7] focus:shadow-[0_0_0_1px_#002FA7] flex items-center justify-between gap-2"
      >
        <span className={selected ? 'text-stone-900' : 'text-stone-400'}>
          {selected ? selected.label : placeholder || 'Select...'}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span onClick={handleClear} className="p-0.5 hover:bg-stone-200 rounded-sm text-stone-400 hover:text-stone-600">
              <X size={12} />
            </span>
          )}
          <CaretDown size={14} className="text-stone-400" />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 shadow-lg max-h-64 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-100">
            <MagnifyingGlass size={14} className="text-stone-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="flex-1 text-sm outline-none bg-transparent text-stone-900 placeholder:text-stone-400"
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-stone-400 text-center">No results</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleSelect(o.value)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    o.value === value
                      ? 'bg-[#002FA7]/5 text-[#002FA7] font-medium'
                      : 'text-stone-700 hover:bg-stone-50'
                  }`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
